import React, { useCallback, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { useAnimatedTab } from "./AnimatedTab.hooks";

interface Tab {
  title: string;
  id: string;
}

interface Props {
  tabs: Tab[];
  activeTab: string;
  gap?: number;
  minWidth?: number;
  className?: string;
  onTabChange: (tabId: string) => void;
}

const TabRoot = styled.div`
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  /* 스크롤바 숨김 (iOS, Chrome 등) */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  /* CSS 변수 초기화 */
  --underline-width: 0px;
  --underline-left: 0px;
`;

const TabList = styled.div<{ $gap: number }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ $gap }) => $gap}px;
  width: fit-content;
  min-width: 100%;
`;

const TabButton = styled.button<{ $active: boolean; $minWidth?: number }>`
  display: flex;
  align-items: flex-start;
  position: relative;
  height: 32.97px;
  font-size: 14.59px;
  line-height: 19.45px;
  letter-spacing: 0px;
  cursor: pointer;
  background: none;
  border: none;
  outline: none;
  justify-content: ${({ $minWidth }) =>
    $minWidth && $minWidth > 0 ? "center" : "flex-start"};
  min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : "unset")};
  flex: 1;

  ${({ $active }) =>
    $active
      ? css`
          font-weight: bold;
          color: #1f6aff;
        `
      : css`
          font-weight: 600;
          color: #6c727f;
        `}
`;

const TabUnderline = styled.div`
  position: absolute;
  bottom: -1px;
  height: 2.43px;
  border-radius: 1px;
  background: #1f6aff;
  transition:
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: var(--underline-width);
  transform: translateX(var(--underline-left));
  pointer-events: none;
`;

const TabButtonUnderline = styled.div`
  width: 100%;
  position: absolute;
  bottom: -1px;
  height: 2px;
  border-radius: 1px;
  background: #1f6aff;
  pointer-events: none;
`;

export function AnimatedTab({
  tabs,
  activeTab,
  gap = 32,
  minWidth = 0,
  className,
  onTabChange,
}: Props) {
  const { current, addRefs } = useAnimatedTab(activeTab);
  const tabRootRef = useRef<HTMLDivElement>(null);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // CSS 변수 업데이트 (디바운싱 적용)
  const updateUnderlineCSS = useCallback(() => {
    if (!current || !tabRootRef.current) return;

    // 이전 타임아웃 취소 (디바운싱)
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      tabRootRef.current?.style.setProperty(
        "--underline-width",
        `${current.offsetWidth}px`
      );
      tabRootRef.current?.style.setProperty(
        "--underline-left",
        `${current.offsetLeft}px`
      );
    }, 0);
  }, [current]);

  // 리사이징, DOM 변경, 탭 변경 시 CSS 변수 업데이트
  useEffect(() => {
    if (!tabRootRef.current) return;

    // 초기 업데이트
    updateUnderlineCSS();

    // ResizeObserver: 부모 컨테이너 크기 변경 감지
    const resizeObserver = new ResizeObserver(() => {
      updateUnderlineCSS();
    });
    resizeObserver.observe(tabRootRef.current);

    // MutationObserver: 탭이 추가/제거되거나 DOM 구조 변경 감지
    const mutationObserver = new MutationObserver(() => {
      updateUnderlineCSS();
    });
    mutationObserver.observe(tabRootRef.current, {
      childList: true, // 자식 노드 추가/제거 감지
      subtree: true, // 모든 자손 노드 변경 감지
      characterData: false, // 텍스트 변경은 감지하지 않음
    });

    // window 리사이즈 이벤트 감지
    const handleWindowResize = () => {
      updateUnderlineCSS();
    };
    window.addEventListener("resize", handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [updateUnderlineCSS]);

  const handleClick = (tabId: string) => {
    onTabChange(tabId);
  };

  return (
    <TabRoot ref={tabRootRef}>
      <TabList $gap={gap} className={className}>
        {tabs.map((tab, i) => (
          <TabButton
            key={i}
            $active={tab.id === activeTab}
            $minWidth={minWidth}
            onClick={() => handleClick(tab.id)}
            ref={(instance) => {
              addRefs(instance, tab.id);
            }}
          >
            {tab.title}
            {tab.id === activeTab && current && current.offsetWidth === 0 && (
              <TabButtonUnderline />
            )}
          </TabButton>
        ))}
      </TabList>

      {activeTab && <TabUnderline />}
    </TabRoot>
  );
}
